import { FormItem, useOnChange } from '@framework/form';
import { Space } from 'antd';
import { SizeType } from 'antd/es/config-provider/SizeContext';
import * as React from 'react';
import { useMemo } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import styled from 'styled-components';
import {
  isObjectsArrayField,
  isSameFieldDatatype,
} from '../../../datatype/Datatype';
import { IReference } from '../../../references/IReference';
import { ExpressionReferenceInput } from '../../../references/input/ExpressionReferenceInput';
import { IConditionOperatorArgument } from '../../operators/IConditionOperator';
import { OperatorsRepository } from '../../operators/OperatorsRepository';
import { ConditionOperatorInput } from '../../operators/input/ConditionOperatorInput';
import { IConditionExpression } from '../IConditionExpression';
import { ConditionArgumentInput } from './ConditionArgumentInput';

const StyeledFormItem = styled(FormItem)`
  &&& {
    .ant-form-item-control-input-content {
      display: flex;
      flex-direction: column;
    }
  }
`;

export interface IConditionInputProps {
  references: IReference[];
  name: string;
  size?: SizeType;
}

/**
 * Indica se un argomento è inline (quindi interno, nel rendering, allo `Space.Compact`)
 * o no (quindi esterno, es. va a capo).
 */
const isInlineArgument = (arg: IConditionOperatorArgument) =>
  arg.type !== 'arrayMatching';

const isRowArgument = (
  arg: IConditionOperatorArgument,
  condition: IConditionExpression,
) =>
  arg.type === 'arrayMatching' &&
  isObjectsArrayField(condition.reference.field);

/**
 * Permette di definire una condizione su un riferimento ad un'altro widget.
 */
export function ConditionInput(props: IConditionInputProps) {
  const { name, references, size } = props;

  const methods = useFormContext();
  const condition: IConditionExpression | null = useWatch({
    name: props.name,
    defaultValue: { reference: null }, // TODO Verificare se ne abbiamo effetivamente bisogno
  });

  const operator = useMemo(
    () => OperatorsRepository.get(condition?.operator),
    [condition?.operator],
  );
  const argsIndexed = useMemo(
    () => operator?.args.map((arg, i) => ({ arg, index: i })) ?? [],
    [operator?.args],
  );

  /**
   * Se cambia il riferimento, azzera l'operatore e gli argomenti.
   */
  useOnChange(condition?.reference?.id, (prevId, nextId) => {
    if (!prevId) return;

    const prevReference = references.find(({ id }) => id === prevId);
    const nextReference = references.find(({ id }) => id === nextId);

    // Evitiamo possibili errori di coerenza fra i dati
    if (!prevReference || !nextReference) return;

    if (!isSameFieldDatatype(prevReference?.field, nextReference?.field)) {
      methods.setValue(`${name}.operator`, null);
      methods.setValue(`${name}.arguments`, []);
    }
  });

  return (
    <StyeledFormItem
      name={name}
      hasFeedback={false}
      layout={{ removeHelpMargin: true }}
    >
      <Space.Compact size={size ?? 'middle'}>
        <ExpressionReferenceInput
          size={size ?? 'middle'}
          name={`${name}.reference`}
          references={references}
        />
        {condition?.reference && (
          <ConditionOperatorInput
            name={`${name}.operator`}
            field={condition.reference.field}
          />
        )}
        {condition &&
          argsIndexed
            .filter(({ arg }) => !isRowArgument(arg, condition))
            .map(({ arg, index }) => (
              <ConditionArgumentInput
                key={index}
                size={size ?? 'middle'}
                name={`${name}.arguments[${index}]`}
                condition={condition}
                arg={arg}
                references={references}
              />
            ))}
      </Space.Compact>
      {condition &&
        argsIndexed
          .filter(({ arg }) => isRowArgument(arg, condition))
          .map(({ arg, index }) => (
            <div key={index}>
              <ConditionArgumentInput
                key={index}
                size={size ?? 'middle'}
                name={`${name}.arguments[${index}]`}
                condition={condition}
                arg={arg}
                references={references}
              />
            </div>
          ))}
    </StyeledFormItem>
  );
}
